import { App } from '@capacitor/app';
import { Network, type GetStatusResult } from '@capawesome/capacitor-network';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import { angularInjectorProvider } from 'Injector';

const INITIAL_STATUS_TIMEOUT_MS = 2000;

// `internetReachable` is Google's validation check on Android, which fails in some regions where our
// servers are reachable.
const isOnline = (status: GetStatusResult) => status.connected;

let pluginOnline: boolean | undefined;
let initialStatusSettled = false;
let loggedReadError = false;
let loggedRefreshError = false;
let latestRefresh = 0;
let resolveInitialStatus = () => {};
const initialStatus = new Promise<void>(resolve => {
    resolveInitialStatus = resolve;
});

const NetworkConnection = {
    get online(): boolean {
        if (!window.CORDOVA) return window.navigator.onLine;
        if (window.RUNNING_IN_TEST_MODE) return true;
        return pluginOnline ?? window.navigator.onLine;
    },

    get offline(): boolean {
        return !this.online;
    },

    // Resolves on the first applied status, a failed read before any status, or the timeout.
    initialized: initialStatus,
};

function logError(message: string, error?: unknown) {
    const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
    ErrorLogService?.notify(message, error);
}

function settleInitialStatus() {
    initialStatusSettled = true;
    resolveInitialStatus();
}

function readFailed(error: unknown) {
    if (pluginOnline === undefined) {
        settleInitialStatus();
        if (loggedReadError) return;
        loggedReadError = true;
        logError('Could not read the network status from @capawesome/capacitor-network', error);
        return;
    }
    if (loggedRefreshError) return;
    loggedRefreshError = true;
    logError('Could not refresh the network status from @capawesome/capacitor-network', error);
}

function applyStatus(status: GetStatusResult) {
    const wasOnline = NetworkConnection.online;
    pluginOnline = isOnline(status);
    if (!initialStatusSettled) {
        settleInitialStatus();
        return;
    }
    // The WebView also fires these from navigator.onLine, which can lag, so a listener can get a duplicate.
    if (wasOnline && !pluginOnline) window.dispatchEvent(new Event('offline'));
    if (!wasOnline && pluginOnline) window.dispatchEvent(new Event('online'));
}

function refreshStatus() {
    latestRefresh += 1;
    const refresh = latestRefresh;
    Network.getStatus().then(status => {
        if (refresh === latestRefresh) applyStatus(status);
    }, readFailed);
}

if (window.CORDOVA) {
    // After the timeout, the bootstrap continues with navigator.onLine, so a later status dispatches an event.
    setTimeout(() => {
        if (initialStatusSettled) return;
        logError('Timed out waiting for the initial network status from @capawesome/capacitor-network');
        settleInitialStatus();
    }, INITIAL_STATUS_TIMEOUT_MS);

    // The plugin sends a change only after the listener registers, so the first read comes after it.
    Network.addListener('networkStatusChange', status => {
        latestRefresh += 1;
        applyStatus(status);
    }).catch(error =>
        logError('Could not listen for network status changes from @capawesome/capacitor-network', error),
    );

    refreshStatus();

    // The listener can miss a change made while the app is in the background.
    App.addListener('resume', refreshStatus).catch(error =>
        logError('Could not listen for the app resume event to refresh the network status', error),
    );
} else {
    settleInitialStatus();
}

export default NetworkConnection;
